import ClockIcon from '@ordr-as/icons/ClockIcon';
import type { ExpoOrder } from '../types';
import CardHeadTime from './CardHeadTime';
import StationChip from './StationChip';

type Props = {
  order: ExpoOrder;
  stationNameById: Map<number, string>;
  nowMs: number;
  selected: boolean;
  focused: boolean;
  onOpen: () => void;
};

const ProgressPill = ({ order }: { order: ExpoOrder }) => {
  if (order.allDone) {
    return (
      <span className="inline-flex items-center text-extra-small font-semibold whitespace-nowrap px-2 py-0.5 rounded-full bg-green text-green-darkest border border-brand-white">
        Ready
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-extra-small font-semibold whitespace-nowrap px-2 py-0.5 rounded-full bg-yellow-lightest text-brand-secondary">
      {order.doneCount}/{order.totalCount}
    </span>
  );
};

const ExpoOrderCard = ({ order, stationNameById, nowMs, selected, focused, onOpen }: Props) => {
  const items = order.displayItems;

  const cardClass = selected
    ? 'bg-purple-lightest ring-2 ring-brand-primary'
    : focused
      ? 'bg-brand-white ring-2 ring-brand-primary'
      : 'bg-brand-white';
  const headClass = order.allDone && !selected ? 'bg-green-lightest' : '';

  return (
    <div
      data-kds-card
      data-card-id={order.groupId}
      className={`kds-card-in rounded flex flex-col overflow-hidden cursor-pointer ${cardClass}`}
      onClick={onOpen}
    >
      <div className={`flex items-center gap-2 px-4 py-3 border-b border-grey-lightest ${headClass}`}>
        <span className="text-small font-semibold text-grey-dark">#{order.orderNumber ?? order.bookingOrderId}</span>
        {order.eatIn && (
          <span className="inline-flex items-center text-extra-small font-semibold whitespace-nowrap px-2 py-0.5 rounded-full bg-grey-lightest text-grey-dark border border-brand-white">
            Eat in
          </span>
        )}
        <ProgressPill order={order} />
        <CardHeadTime createdUtc={order.createdUtc} nowMs={nowMs} />
      </div>

      <div className="px-4 py-3 flex flex-col gap-3 flex-1">
        {items.map((item) => (
          <div key={item.key} className="flex flex-col gap-0.5">
            <div className="flex gap-2 items-baseline">
              <span className="font-bold min-w-5 text-brand-black">{item.quantity}×</span>
              <span className="font-bold flex-1 text-brand-black">{item.name}</span>
            </div>
            {item.modifiers.length > 0 && (
              <ul className="m-0 p-0 pl-5 list-none flex flex-col gap-0.5">
                {item.modifiers.map((modifier, index) => (
                  <li key={index} className="text-small text-grey-dark flex gap-1 items-baseline">
                    <span className="text-grey-medium">→</span>
                    {modifier}
                  </li>
                ))}
              </ul>
            )}
            {item.comment && <div className="pl-5 text-small italic text-grey-dark">"{item.comment}"</div>}
            <div className="flex gap-1 flex-wrap pl-5 mt-1">
              {item.chips.map((chip, index) => (
                <StationChip
                  key={index}
                  label={stationNameById.get(chip.stationId) ?? 'Unknown station'}
                  state={chip.state}
                />
              ))}
            </div>
          </div>
        ))}
        {order.orderComment && <div className="text-small italic text-grey-dark">"{order.orderComment}"</div>}
      </div>

      {!order.allDone && (
        <div className="px-4 pb-4 pt-1 flex">
          <div className="h-7 w-full flex items-center gap-2 text-small italic text-grey-dark">
            <ClockIcon width={16} height={16} />
            In progress · {order.doneCount}/{order.totalCount} steps
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpoOrderCard;
